"use client";

import type { MouseEvent } from "react";
import Image from "next/image";
import Link from "next/link";
import { Cormorant_Garamond } from "next/font/google";
import { motion } from "motion/react";
import { ParallaxBanner, ParallaxProvider } from "react-scroll-parallax";
import type { BannerLayer } from "react-scroll-parallax";

const displaySerif = Cormorant_Garamond({
  subsets: ["latin"],
  weight: ["500", "600", "700"],
});

const ease = [0.16, 1, 0.3, 1] as const;

function FadeUp({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  return (
    <motion.div className={className} initial={{ y: 28 }} animate={{ y: 0 }} transition={{ duration: 0.9, delay, ease }}>
      {children}
    </motion.div>
  );
}

export default function ReMindHeroSectionEn() {
  const handleScrollToOverview = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById("retreat-overview")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const background: BannerLayer = {
    translateY: [0, 25],
    scale: [1.05, 1, "easeOutCubic"],
    shouldAlwaysCompleteAnimation: true,
    children: (
      <div className="absolute inset-0">
        <Image src="/reconnect/6.jpg" alt="Reconnect Glamping — aerial view, Moldova, Romania" fill priority sizes="100vw" className="object-cover object-center" />
      </div>
    ),
  };

  const overlays: BannerLayer = {
    expanded: false,
    children: (
      <>
        <div className="absolute inset-0 bg-black/38" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/52 via-black/28 to-[#0a1510]/82" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_50%,rgba(0,0,0,0.32)_100%)]" />
      </>
    ),
  };

  const headline: BannerLayer = {
    translateY: [0, 12],
    shouldAlwaysCompleteAnimation: true,
    expanded: false,
    children: (
      <div className="absolute inset-0 flex items-center justify-center px-6 sm:px-10">
        <div className="max-w-5xl text-center text-white">
          <FadeUp delay={0.1}>
            <p className="mb-6 font-[family-name:var(--font-plus-jakarta)] text-[0.6rem] font-semibold uppercase tracking-[0.52em] text-white/50 sm:text-[0.65rem]">
              Mindtale × Reconnect Glamping
            </p>
          </FadeUp>
          <FadeUp delay={0.25}>
            <div className="mb-6 flex items-baseline justify-center font-[family-name:var(--font-plus-jakarta)] text-[3.4rem] font-extrabold leading-none tracking-[-0.03em] sm:text-[5rem] lg:text-[6.5rem]">
              <span className="relative" style={{ color: "#006D77", filter: "drop-shadow(0 0 32px rgba(0,109,119,0.55))" }}>Re</span>
              <span className="text-white">Mind</span>
            </div>
          </FadeUp>
          <FadeUp delay={0.38}>
            <div className="mx-auto mb-7 h-px w-16 bg-white/20" />
          </FadeUp>
          <FadeUp delay={0.48}>
            <h1 className={`${displaySerif.className} text-[1.9rem] font-semibold leading-[1.1] tracking-tight text-white/95 sm:text-4xl md:text-5xl lg:text-[3.6rem]`}>
              Your leaders deserve to rediscover themselves.
            </h1>
          </FadeUp>
          <FadeUp delay={0.6}>
            <p className="mx-auto mt-5 max-w-lg font-[family-name:var(--font-plus-jakarta)] text-[0.78rem] font-light leading-relaxed tracking-[0.08em] text-white/60 sm:text-sm">
              3 days · 2 nights · small group · clinical facilitation · nature
            </p>
          </FadeUp>
          <FadeUp delay={0.74} className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <a href="#contact" className="inline-flex items-center justify-center rounded-full px-8 py-3.5 font-[family-name:var(--font-plus-jakarta)] text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-white shadow-[0_8px_32px_rgba(0,109,119,0.45)] transition-all duration-200 hover:shadow-[0_8px_40px_rgba(0,109,119,0.65)] hover:scale-[1.03] active:scale-95" style={{ backgroundColor: "#006D77" }}>
              Request a quote
            </a>
            <a href="#retreat-overview" onClick={handleScrollToOverview} className="inline-flex items-center justify-center rounded-full border border-white/25 px-8 py-3.5 font-[family-name:var(--font-plus-jakarta)] text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-white/75 backdrop-blur-sm transition-all duration-200 hover:border-white/55 hover:text-white hover:scale-[1.03] active:scale-95">
              Explore the program
            </a>
          </FadeUp>
        </div>
      </div>
    ),
  };

  return (
    <ParallaxProvider>
      <ParallaxBanner layers={[background, overlays, headline]} className="relative isolate h-screen" style={{ height: "100svh" }}>
        {/* Back to Mindtale */}
        <a href="/" className="group absolute left-5 top-5 z-20 flex items-center gap-2 rounded-full border border-white/20 bg-black/20 px-4 py-2 backdrop-blur-sm transition-all duration-200 hover:border-white/40 hover:bg-black/30 sm:left-8 sm:top-8">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform duration-200 group-hover:-translate-x-0.5">
            <path d="M11 7H3M3 7L6.5 3.5M3 7L6.5 10.5" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.7"/>
          </svg>
          <span className="font-[family-name:var(--font-plus-jakarta)] text-[0.62rem] font-medium uppercase tracking-[0.22em] text-white/70">Mindtale</span>
        </a>
        {/* Language toggle */}
        <div className="absolute right-5 top-5 z-20 flex items-center gap-1 rounded-full border border-white/20 bg-black/20 p-1 backdrop-blur-sm sm:right-8 sm:top-8">
          <Link href="/remind" className="rounded-full px-3 py-1 font-[family-name:var(--font-plus-jakarta)] text-[0.62rem] font-medium uppercase tracking-[0.18em] text-white/50 transition-colors hover:text-white/80">RO</Link>
          <span className="rounded-full bg-white/15 px-3 py-1 font-[family-name:var(--font-plus-jakarta)] text-[0.62rem] font-medium uppercase tracking-[0.18em] text-white">EN</span>
        </div>
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-44 bg-gradient-to-t from-black/58 via-black/18 to-transparent" />
        <a href="#retreat-overview" className="group absolute bottom-10 left-1/2 z-20 -translate-x-1/2 text-center" aria-label="Scroll down" onClick={handleScrollToOverview}>
          <span className="block font-[family-name:var(--font-plus-jakarta)] text-[0.6rem] uppercase tracking-[0.32em] text-white/55">Scroll</span>
          <span className="mx-auto mt-3 flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-white/8 text-white/70 transition-all duration-300 group-hover:translate-y-1 group-hover:border-white/60 group-hover:bg-white/18">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 2.5V11.5M7 11.5L3.5 8M7 11.5L10.5 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </a>
      </ParallaxBanner>
    </ParallaxProvider>
  );
}
